import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Loader2, Power, PauseCircle, PlayCircle, Trash2, Phone } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", mobile: "", whatsapp_number: "", password: "", team: "Sales" };

function EmployeeCalls() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/analytics/employee-calls").then((r) => setRows(r.data.items)).catch(() => {}); }, []);
  const cols = [["today", "Today"], ["yesterday", "Yesterday"], ["d3", "3 Days"], ["d7", "7 Days"], ["d30", "30 Days"]];
  return (
    <div className="glass rounded-xl overflow-hidden" data-testid="employee-calls-panel">
      <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /><h2 className="font-semibold text-sm">Calls by Employee</h2></div>
      {!rows ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> :
        rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No call activity yet.</div> :
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b border-slate-800"><tr><th className="px-4 py-2.5 text-left">Employee</th>{cols.map(([k, l]) => <th key={k} className="px-4 py-2.5 text-right">{l}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (<tr key={r.employee_id} data-testid={`emp-calls-${r.employee_id}`} className="border-t border-slate-800/60">
            <td className="px-4 py-2.5">{r.name}</td>{cols.map(([k]) => <td key={k} className="px-4 py-2.5 text-right font-mono">{r[k]}</td>)}</tr>))}</tbody>
        </table></div>}
    </div>
  );
}

export default function Employees() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/employees").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/employees", form);
      toast.success("Employee created");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/employees/${id}`, body);
      toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete employee "${name}"? Their assigned leads will be unassigned (lead history is preserved). This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/employees/${id}`);
      toast.success(`Employee deleted — ${data.leads_unassigned} lead(s) unassigned`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Employees</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage telecallers, activation & lead assignment state.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-employee-button" className="bg-primary text-primary-foreground"><UserPlus className="h-4 w-4 mr-1.5" />Add Employee</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              {[
                ["name", "Full Name", "text"], ["email", "Email", "email"],
                ["mobile", "Mobile", "text"], ["whatsapp_number", "WhatsApp Number", "text"],
                ["password", "Password", "password"], ["team", "Team", "text"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <Label className="text-xs text-muted-foreground">{label}</Label>
                  <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`employee-${k}-input`} className="bg-slate-900/60 border-slate-700 mt-1" />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="save-employee-button" className="bg-primary text-primary-foreground">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <EmployeeCalls />

      <div className="glass rounded-xl overflow-hidden">
        {!items ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-4 py-3">Accepting Leads</th>
                  <th className="px-4 py-3">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <div className="font-medium">{e.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{e.employee_id} · {e.team}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      <div>{e.email}</div><div className="font-mono">{e.mobile}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" data-testid={`toggle-account-${e.id}`}
                        onClick={() => patch(e.id, { account_status: e.account_status === "active" ? "inactive" : "active" }, "Account status updated")}
                        className={e.account_status === "active" ? "text-emerald-400" : "text-red-400"}>
                        <Power className="h-4 w-4 mr-1" />{e.account_status}
                      </Button>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" data-testid={`toggle-assignment-${e.id}`}
                        onClick={() => patch(e.id, { assignment_status: e.assignment_status === "accepting" ? "paused" : "accepting" }, "Assignment state updated")}
                        className={e.assignment_status === "accepting" ? "text-emerald-400" : "text-amber-400"}>
                        {e.assignment_status === "accepting" ? <PlayCircle className="h-4 w-4 mr-1" /> : <PauseCircle className="h-4 w-4 mr-1" />}
                        {e.assignment_status}
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(e.last_login)}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" data-testid={`delete-employee-${e.id}`}
                        onClick={() => del(e.id, e.name)} className="text-red-400 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
